import * as _ from 'underscore';
import { FocusCtrl } from '../../common/attributes/tbFocus/FocusCtrl';
import { ListResult } from '../fetchers/ListResult';
import { Keys } from '../helpers/Keys';
import { TagsHelper } from '../helpers/TagsInputHelper';
import { ISliceable } from '../models/ISliceable';
import { ITemplate } from '../models/ITemplate';
import { Module } from '../Module';
import { ITag } from '../ServerApi/types/ITag';
import { SearchSuggestionsService } from '../services/SearchSuggestionsService';

export interface ITagWithCount {
  name: string;
  count?: number; //Number of items the tag applies to. No count means the tag applies to all items
  added?: boolean; // true when this tag was added by the user
}

interface ITagsInputAttributes extends ng.IAttributes {
  ngModel: string;
}

interface ITagsInputScope extends ng.IScope {
  newTag: string;
  currentTags: ITagWithCount[];
  selectedTagIndex: number; // Index of the selected tag

  // tag suggestion controls
  tagSource: ISliceable<ITag>;
  dropdownWidth: number;
  currentRow: {
    index: number;
    object: any;
  };
  cells: ITemplate[];

  //methods
  selectTag(tag: string): void;
  removeTag(tag: string): void;
  addTag(tag: string): void;
  displayTag(tag: string): string;
  displayTagHtml(tag: string): string;
  handleKeyEvent(event: BaseJQueryEventObject): void;
}

Module.instance().directive('tbTagsInput', [
  'SearchSuggestionsService',
  (searchSuggestionsService: SearchSuggestionsService) => {
    return {
      restrict: 'E',
      require: ['ngModel', '?tbFocus'],
      templateUrl: 'tagsInput.html',
      replace: true,
      scope: {
        newTag: '=?textInputModel'
      },
      link: (scope: ITagsInputScope, element: ng.IAugmentedJQuery, attributes: ITagsInputAttributes, controllersArr: any[]) => {

        const ngModelCtrl: ng.INgModelController = controllersArr[0];
        const ctrlFocus: FocusCtrl = controllersArr[1];
        // Initialize
        scope.selectedTagIndex = -1;

        scope.currentRow = {
          index: 0,
          object: null
        };

        // Helper methods

        const deselectTags = () => {
          scope.selectedTagIndex = -1;
        };

        // Scope methods
        scope.addTag = (newTag: string) => {
          if(!newTag) return;

          const quotedNewTag = TagsHelper.quote(newTag);

          scope.removeTag(quotedNewTag);

          ngModelCtrl.$setViewValue(ngModelCtrl.$modelValue.concat([{ name: quotedNewTag, added: true }]));
          deselectTags();
          scope.newTag = '';
        };

        scope.removeTag = (tagName: string) => {
          ngModelCtrl.$setViewValue(_.reject(ngModelCtrl.$modelValue, (tag: ITagWithCount) => {
            return (tag.name === tagName)
                // tagName may be quoted so check if the unquoted version is already in the list
                || (tag.name === TagsHelper.unquote(tagName));
          }));
          scope.selectedTagIndex--;
        };

        scope.selectTag = (tagName: string) => {
          scope.selectedTagIndex = _.indexOf(scope.currentTags, _.findWhere(scope.currentTags, { name: tagName }));
        };

        scope.displayTag = TagsHelper.unquote;
        scope.displayTagHtml = TagsHelper.displayTagHtml;

        const getNewTagName = () => {
          // scope.currentRow.object may have a stale object when scope.newTag goes to an empty string
          if(!scope.newTag || !scope.currentRow.object) return '';
          return scope.currentRow.object.name;
        };

        scope.handleKeyEvent = (event: JQueryInputEventObject) => {
          const selectedTag = scope.currentTags[scope.selectedTagIndex];
          switch(event.which) {

            case Keys.Tab:
              if(!selectedTag) scope.addTag(getNewTagName());
              break;

            case Keys.Enter:
              if(selectedTag) {
                /*
                //TODO: is this a good experience? Or is it confusing?
                // We decided it was confusing for now but we may revisit this later
                scope.removeTag(selectedTag.name);
                deselectTags();
                scope.newTag = selectedTag.name;
                */
              } else {
                scope.addTag(getNewTagName());
              }
              event.preventDefault();
              break;

            case Keys.Backspace:
              if(selectedTag) {
                scope.removeTag(selectedTag.name);
                event.preventDefault();
              } else if((<HTMLInputElement>input.get(0)).selectionStart === 0) {
                scope.selectedTagIndex = scope.currentTags.length - 1;
              }
              break;

            case Keys.Delete:
              if(selectedTag.name) {
                scope.removeTag(selectedTag.name);
                scope.selectedTagIndex++; // Move to the next tag
                event.preventDefault();
              }
              break;

            case Keys.ArrowRight:
              if(selectedTag) {
                scope.selectedTagIndex++;
                event.preventDefault();
              } else {
                deselectTags();
              }
              break;

            case Keys.ArrowLeft:
              if(selectedTag) {
                if(scope.selectedTagIndex > 0) scope.selectedTagIndex--;
                event.preventDefault();
              } else if((<HTMLInputElement>input.get(0)).selectionStart === 0) { // start selecting tags
                scope.selectedTagIndex = scope.currentTags.length - 1;
                event.preventDefault();
              }
              break;

            case Keys.ArrowDown:
              scope.currentRow.index++;
              event.preventDefault();
              break;

            case Keys.ArrowUp:
              // Don't let the index go below 0 on arrow up.  An item must always be selected
              scope.currentRow.index = Math.max(scope.currentRow.index - 1, 0);
              event.preventDefault();
              break;

            default:
              deselectTags();
          }

          // Pass keydown events to the outer element
          if(!event.isDefaultPrevented()) element.trigger(event.type, event);
        };

        // Watchers

        // Get a list of possible tags every time newTag changes. Add newTag into the list if it's not already in the list
        scope.$watch(() => scope.newTag, (newTag: string) => {
          let newTagAddedToList = false;
          let firstSlice = true;

          scope.tagSource = {
            getSlice: (start: number, size: number): ng.IPromise<ListResult<ITag>> => {
              return searchSuggestionsService.tags(newTag).getSlice(start, size).then((result: ListResult<ITag>) => {
                if(firstSlice) {
                  scope.currentRow.index = 0; //Reset the tags to the beginning
                  firstSlice = false;
                }

                if(result.index === 0 && (result.items.length === 0 || result.items[0].name !== scope.newTag)) {
                  result.items.unshift({ name: scope.newTag });
                  newTagAddedToList = true;
                }

                if(newTagAddedToList) {
                  if(result.index !== 0) result.index++;
                  result.total++;
                }

                return result;
              });
            }
          };

          // Ensure the typeahead dropdown matches the width of the tags input when typing new tags
          scope.dropdownWidth = element.width();

          // Simulate server tag splitting logic
          if(!newTag || newTag.split('"').length === 2) return; // If there is only 1 double quote then ignore parsing

          const tagList = TagsHelper.parseTagString(newTag);

          if(tagList.length > 1) {
            _.forEach(tagList.slice(0, -1), scope.addTag);
          }
          scope.newTag = _.last(tagList) || '';
        });

        scope.$watch(() => ngModelCtrl.$modelValue, (newVal: ITagWithCount[]) => {
          scope.currentTags = _.isArray(newVal) ? newVal : [];
          if(scope.selectedTagIndex >= scope.currentTags.length) deselectTags();
        });

        // Element listeners

        const input = element.find('.tb-tag-input');
        if(ctrlFocus) ctrlFocus.setFocusElement(input);

        input.on('blur', () => scope.$apply(deselectTags));

        element.click((event: JQuery.TriggeredEvent) => {
          if(ctrlFocus) ctrlFocus.focus(); //Keep focus on the input element to handle key events
          else input.focus();

          // Deselect tags if they didn't click on a tag
          if($(event.target).closest('.tb-tag-container').length === 0) {
            scope.$apply(deselectTags);
          }
        });
      }
    };
  }
]);
